import type { ComputedPropertySchema } from '../ReplicantComputedProperties';
import type SB from '../SchemaBuilder';
import { unreachable } from './Utils';

export function flattenComputedPropertySchema(
    parentKey: string,
    fields: { [key: string]: ComputedPropertySchema },
): { [key: string]: ComputedPropertySchema } {
    let result: { [key: string]: ComputedPropertySchema } = {};
    for (const key in fields) {
        const field = key as keyof typeof fields;
        const type = fields[field]!;
        if (type.isObjectSchema()) {
            result = { ...result, ...flattenComputedPropertySchema(key, type.getFullSchema()) };
        } else {
            result[parentKey.length > 0 ? parentKey + '>' + field : field] = type;
        }
    }

    return result;
}

export function getNestedProperties(schema: ComputedPropertySchema): { [key: string]: ComputedPropertySchema } | null {
    if (schema?.isArraySchema()) {
        const itemSchema = schema.getItemSchema();

        if (itemSchema.isObjectSchema()) {
            return itemSchema.getFullSchema();
        }
    }

    return null;
}

export function getPrefixBasedOnType(type: ComputedPropertySchema, opts: { index: boolean }): string {
    if (type._extendedType === 'matchableString') {
        return 'm_';
    }

    const indexedPrefix = opts.index ? '' : 'pl_';
    let typePrefix: string;
    switch (type._type) {
        case 'boolean':
            typePrefix = 'b_';
            break;
        case 'number':
            typePrefix = 'n_';
            break;
        case 'integer':
            typePrefix = 'i_';
            break;
        case 'string':
            typePrefix = 'kw_'; // prefix with the ES type instead, since string can mean text or keyword
            break;
        case 'tuple':
            typePrefix = 'kw_';
            break;
        case 'array':
            if (getNestedProperties(type)) {
                typePrefix = 'nst_';
            } else {
                typePrefix =
                    'arr_' +
                    getPrefixBasedOnType((type as SB.ArraySchema<any>).getItemSchema() as ComputedPropertySchema, opts);
            }
            break;
        case 'object': {
            throw new Error('Cannot prefix nested type, something went wrong.');
        }
        default:
            unreachable(type._type);
    }

    return indexedPrefix + typePrefix;
}

/** Returns a query field as `foo.bar` as an internal Opensearch field name as `kw_foo>bar`. */
export function queryFieldNameToOpensearchName(
    field: string,
    fields: { [key: string]: ComputedPropertySchema },
    opts: { index: boolean },
): string {
    const segments = field.split('.') as [string, ...string[]];

    // Check for a flattened object field:
    const flatObjectField = segments.join('>');
    if (flatObjectField in fields) {
        return getPrefixBasedOnType(fields[flatObjectField]!, { index: opts.index }) + flatObjectField;
    }

    const fieldSchema = fields[segments[0]]!;
    const name = getPrefixBasedOnType(fieldSchema, { index: opts.index }) + segments[0];

    if (segments.length === 1) {
        return name;
    } else {
        const schema = fields[segments[0]]!;
        const nestedSchema = getNestedProperties(schema);
        if (nestedSchema) {
            return name + '.' + queryFieldNameToOpensearchName(segments.slice(1).join('.'), nestedSchema, opts);
        } else {
            throw new Error('Nested path is not an array');
        }
    }
}
